import React, { useState, useContext, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Textarea from 'components/Textarea'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import MultiSelectionDropdown from 'components/multiSelectDropdown'
import Input from 'components/Input'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import { delay } from 'utils/delay'
import { SiteContext } from 'providers/site'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import { reasonDefaultOptions } from 'constants/rejectTriggerReason'
import Checkbox from 'components/Checkbox'
import { EcosystemContentFooter } from '../ecosystemData/style'
import Button from 'components/Button'
import CheckIcon from 'assets/icons/check-icon'
import { AssessmentPageSubTittleIconWrapper, WrapperCheckBox } from '../../../assessment/style'
import CriterionEDocsUploader from '../supportingDocs/criterionEDocsUploader'
import useCriterionEPersistence from 'hooks/useCriterionEPersistence'
import { getCriterionEId, hasQualifyingReason, mapTriggerReasonFromApi } from 'utils/criterionEHelpers'
import {
  setDisabled,
  setDisabledQualificationReason,
  setShowReassessmentFields,
  setShowFinishButton,
  hasTriggerReasons,
  getInitialAssessmentYear,
  getInitialIrreplaceabilityScore,
  createCriterionEHandlers,
  resolveCriterionECheckFields,
  isIrreplaceabilityInputDisabled
} from '../../../../../../helpers/criterionE/accordionContent'

const tooltipXOffset = 0
const tooltipYOffset = 16
const inputSaveDelayTime = 300
const updateIrreplaceabilityScoreTimeout = delay(inputSaveDelayTime)
const updateDateOfAssesmentTimeOut = delay(inputSaveDelayTime)
const updateIsCheckedTimeOut = delay(inputSaveDelayTime)
const updateTriggerReasonIdTimeOut = delay(inputSaveDelayTime)
const updateTriggerReasonTextTimeOut = delay(inputSaveDelayTime)
const YEAR_INPUT_LENGTH = 4

const criterionEPropType = PropTypes.shape({
  site_irreplaceability_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  site_irreplaceability_history_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  irreplaceability_assessment_approved: PropTypes.bool,
  irreplaceability_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trigger_reason_id: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  trigger_reason_text: PropTypes.string,
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  last_reassessment_date: PropTypes.string
})

const AccordionContent = ({ site, item: criterionE, updateCriterionE, siteId, toggleAccordion, defaultPage = false, criterionEs = [] }) => {
  const params = useParams()
  const checkedValue = useRef([])
  const { siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const criterionEId = getCriterionEId(criterionE)
  const checkFields = resolveCriterionECheckFields(
    siteProposalFieldValidation,
    criterionEId,
    defaultSiteProposalValidationResults.criterionE
  )
  
  const [isChecked, setIsChecked] = useState(criterionE?.irreplaceability_assessment_approved || false)
  const [irreplaceabilityScore, setIrreplaceabilityScore] = useState(criterionE?.irreplaceability_score || '')
  const [checkedOptions, setCheckedOptions] = useState(reasonDefaultOptions)
  const [qualificationReason, setQualificationReason] = useState(mapTriggerReasonFromApi(criterionE?.trigger_reason_id))
  const qualified = hasQualifyingReason(criterionE?.trigger_reason_id)

  const [reassessmentRational, setReassessmentRational] = useState(criterionE?.trigger_reason_text || '')
  const [assessmentDate, setAssessmentDate] = useState(criterionE?.year || '')
  const disabled = setDisabled(site, qualified, isEditable)
  const disabledQualificationReason = setDisabledQualificationReason(site, isEditable)
  const showReassessmentFields = setShowReassessmentFields(criterionE, site, defaultPage)
  const showFinishButton = setShowFinishButton(site, defaultPage)
  const canFinishWithReasons = hasTriggerReasons(criterionE?.trigger_reason_id)
  const { persistField } = useCriterionEPersistence({
    siteId,
    siteHistoryId: params.siteHistoryId,
    criterionEId,
    updateCriterionE
  })

  // On component mount, extract only the year from the full date saved in the database
  useEffect(() => {
    try {
      const savedYear = getInitialAssessmentYear(criterionE?.year)
      if (savedYear) {
        setAssessmentDate(savedYear)
      }

      const initialScore = getInitialIrreplaceabilityScore(criterionE?.irreplaceability_score, (value) => Number.isInteger(Number(value)))
      if (initialScore !== null) {
        setIrreplaceabilityScore(initialScore)
      }
    } catch (error) {
      console.error('Error initializing criterion E form:', error)
    }
  }, [criterionE?.year, criterionE?.irreplaceability_score])

  const {
    handleChangeValueChecked,
    handleChangeQualificationReason,
    handleChangeReassessmentRational,
    handleCheckboxIrreplaceabilityChange,
    handleChangeIrreplaceabilityScore,
    handleChangeAssessmentDate
  } = useMemo(() => createCriterionEHandlers({
    checkedValue,
    setCheckedOptions,
    setQualificationReason,
    setIsChecked,
    setReassessmentRational,
    setIrreplaceabilityScore,
    setAssessmentDate,
    persistField,
    updateTriggerReasonIdTimeOut,
    updateTriggerReasonTextTimeOut,
    updateIsCheckedTimeOut,
    updateIrreplaceabilityScoreTimeout,
    updateDateOfAssesmentTimeOut,
    yearInputLength: YEAR_INPUT_LENGTH
  }), [persistField, isChecked])

  return (
    <Container width='100%' padding='0px'>
      <div data-test="irreplaceability">
        <Row>
          <Col md={12}>
            <WrapperCheckBox disabled={disabled}>
              <Checkbox
                label={<FormattedMessage id="checkboxIrreplaceability" />}
                onChange={() => handleCheckboxIrreplaceabilityChange(isChecked)}
                checked={isChecked}
                field="checkboxIrreplaceability"
                disabled={disabled}
                dataTest="irreplaceability-checkbox"
                error={
                  checkFields?.irreplaceability_assessment_approved
                    ? {
                      field: '',
                      error: ''
                    }
                    : { field: 'checkboxIrreplaceability', error: 'pleaseCompleteThisField' }
                }
              />
              <Tooltip
                style={{ zIndex: 1000 }}
                placement="bottom"
                trigger={['hover']}
                align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                overlay={{ label: 'irreplaceabilityCheckboxLabel', infoTooltip: 'irreplaceabilityCheckboxHint' }}
                type="big"
              >
                <AssessmentPageSubTittleIconWrapper>
                  <InfoIcon />
                </AssessmentPageSubTittleIconWrapper>
              </Tooltip>
            </WrapperCheckBox>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Input
              dataTestId="irreplaceability-score-input"
              value={irreplaceabilityScore}
              onChange={handleChangeIrreplaceabilityScore}
              infoTooltip="irreplaceabilityScoreHint"
              placeholder="enterIrreplaceabilityScore"
              label="irreplaceabilityScore"
              type='decimal'
              field="irreplaceabilityScore"
              disabled={isIrreplaceabilityInputDisabled(site, isChecked)}
              error={
                checkFields?.irreplaceability_score
                  ? {
                    field: '',
                    error: ''
                  }
                  : { field: 'irreplaceabilityScore', error: 'pleaseCompleteThisField' }
              }
            />
          </Col>
          <Col md={6}>
            <Input
              value={assessmentDate}
              onChange={handleChangeAssessmentDate}
              infoTooltip="irreplaceabilityAssessmentDateHint"
              placeholder="enterAssessmentDate"
              label="irreplaceabilityAssessmentDateLabel"
              field="irreplaceabilityAssessmentDate"
              type='number'
              disabled={isIrreplaceabilityInputDisabled(site, isChecked)}
              error={
                checkFields?.year
                  ? {
                    field: '',
                    error: ''
                  }
                  : { field: 'irreplaceabilityAssessmentDate', error: 'pleaseCompleteThisField' }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <CriterionEDocsUploader site={site} criterionEs={criterionEs} criterionE={criterionE} />
          </Col>
        </Row>
        <Row>
          {showReassessmentFields && (
            <Col md={6}>
              <MultiSelectionDropdown
                value={qualificationReason}
                saveChanges={handleChangeQualificationReason}
                valueChecked={handleChangeValueChecked}
                label='triggerEcosystemLabel'
                infoTooltip='triggerLabelHint'
                field='qualificationReason'
                error={qualificationReason?.length === 0 ? {
                  field: 'qualificationReason',
                  error: 'pleaseCompleteThisField'
                } : { field: '', error: '' }}
                secondStepOptions={checkedOptions}
                placeholder='criterionCTriggerPlaceholder'
              />
            </Col>
          )}
          {showReassessmentFields && (
            <Col md={6}>
              <Textarea
                value={reassessmentRational}
                onChange={handleChangeReassessmentRational}
                placeholder='justificationNoChangeCriterionCPlaceholder'
                label='justificationNoChange'
                infoTooltip='qualificationReasonHint'
                field='rationalForReassessment'
                height='128px'
                disabled={disabledQualificationReason}
              />
            </Col>
          )}
        </Row>

        {(showFinishButton && Boolean(criterionE?.last_reassessment_date)) && (
          <Row>
            <Col md={12}>
              <EcosystemContentFooter>
                <Button height='40px' isDisabled={!canFinishWithReasons} onClick={toggleAccordion}
                  type={canFinishWithReasons ? 'primary' : 'disabledPrimary'}>
                  <CheckIcon />
                  <FormattedMessage id='finishEditing' />
                </Button>
              </EcosystemContentFooter>
            </Col>
          </Row>
        )}
        {(showFinishButton && !criterionE?.last_reassessment_date) && (
          <Row>
            <Col md={12}>
              <EcosystemContentFooter>
                <Button height='40px' onClick={toggleAccordion}
                  type='primary'>
                  <CheckIcon />
                  <FormattedMessage id='finishEditing' />
                </Button>
              </EcosystemContentFooter>
            </Col>
          </Row>
        )}
      </div>
    </Container>
  )
}

export default AccordionContent

AccordionContent.propTypes = {
  site: PropTypes.shape({
    SiteStatusId: PropTypes.number.isRequired,
    reassessmentFlag: PropTypes.bool
  }).isRequired,
  item: criterionEPropType.isRequired,
  updateCriterionE: PropTypes.func.isRequired,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  toggleAccordion: PropTypes.func,
  defaultPage: PropTypes.bool,
  criterionEs: PropTypes.arrayOf(criterionEPropType)
}
